import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Link2, CheckCircle2, AlertTriangle, RefreshCw, Unlink, Activity } from "lucide-react";
import { toast } from "sonner";

export default function AgencyPairingPage() {
  const [hubUrl, setHubUrl] = useState("");
  const [code, setCode] = useState("");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/agency/pairing-status");
      setStatus(data);
      if (!hubUrl && data?.hub_url) setHubUrl(data.hub_url);
      if (!code && data?.pairing_code) setCode(data.pairing_code);
    } catch (e) {
      toast.error("Impossible de charger le statut d'appairage");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const submit = async () => {
    if (!hubUrl.trim() || !code.trim()) {
      toast.error("URL du HUB et code requis");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/agency/claim", {
        hub_url: hubUrl.trim(),
        pairing_code: code.trim(),
      });
      if (data.ok || data.ack_ok) {
        toast.success("Site appairé au HUB ✅");
      } else {
        toast.error(`Échec de l'appairage : ${data.message || "HUB indisponible"}`);
      }
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      toast.error(`Erreur : ${msg}`);
    } finally {
      setBusy(false);
    }
  };

  const heartbeat = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/agency/heartbeat-now");
      if (data.ok) toast.success("Heartbeat envoyé ✓");
      else toast.error(`Heartbeat KO : ${data.error || data.status || "erreur"}`);
      await load();
    } catch (e) {
      toast.error("Erreur heartbeat");
    } finally {
      setBusy(false);
    }
  };

  const unpair = async () => {
    if (!window.confirm("Désappairer ce site du HUB Agence ?")) return;
    setBusy(true);
    try {
      await api.post("/agency/unpair");
      toast.success("Site désappairé");
      setHubUrl(""); setCode("");
      await load();
    } catch {
      toast.error("Erreur");
    } finally {
      setBusy(false);
    }
  };

  const paired = !!status?.paired;

  return (
    <div className="p-6 md:p-10 max-w-4xl" data-testid="admin-agency-pairing-page">
      <header className="flex items-center justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brand-sky)] font-semibold mb-1">Cockpit</div>
          <h1 className="font-display text-3xl font-bold text-[color:var(--brand-navy)]">Appairage HUB Agence</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">
            Connectez ce site au HUB Agence MSC pour le piloter à distance (statut, heartbeat, monitoring).
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="agency-refresh-btn">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Actualiser
        </Button>
      </header>

      {/* Status banner */}
      <Card
        className={`p-5 mb-6 border-l-4 ${paired ? "border-l-green-500 bg-green-50/40" : "border-l-amber-500 bg-amber-50/40"}`}
        data-testid="agency-status-card"
      >
        <div className="flex items-start gap-3">
          {paired ? (
            <CheckCircle2 className="w-6 h-6 text-green-600 shrink-0" />
          ) : (
            <AlertTriangle className="w-6 h-6 text-amber-600 shrink-0" />
          )}
          <div className="flex-1">
            <div className="font-display font-bold text-lg" data-testid="agency-status-label">
              {paired ? "Site appairé ✅" : "Non appairé"}
            </div>
            {paired ? (
              <div className="text-sm text-[hsl(var(--muted-foreground))] mt-1 space-y-0.5">
                <div><span className="font-semibold">HUB :</span> {status.hub_url}</div>
                <div><span className="font-semibold">Code :</span> <code className="bg-white px-1.5 py-0.5 rounded border">{status.pairing_code}</code></div>
                <div><span className="font-semibold">URL pod :</span> {status.pod_url}</div>
                <div><span className="font-semibold">Appairé le :</span> {status.paired_at ? new Date(status.paired_at).toLocaleString("fr-FR") : "—"}</div>
                <div className="flex items-center gap-2 mt-1">
                  <Activity className="w-3.5 h-3.5 text-[color:var(--brand-sky)]" />
                  <span className="font-semibold">Dernier heartbeat :</span>{" "}
                  {status.last_heartbeat_at ? new Date(status.last_heartbeat_at).toLocaleString("fr-FR") : "jamais"}
                  {status.last_heartbeat_status && (
                    <span className={`ml-1 text-xs px-2 py-0.5 rounded-full ${status.last_heartbeat_status === "ok" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                      {status.last_heartbeat_status}
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
                {status?.last_ack_message
                  ? <>Dernière tentative : <em>{status.last_ack_message}</em></>
                  : <>Renseignez l'URL du HUB et le code d'appairage transmis par l'agence.</>}
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Pairing form */}
      <Card className="p-6 mb-6">
        <h2 className="font-display font-bold text-xl mb-1 flex items-center gap-2">
          <Link2 className="w-5 h-5 text-[color:var(--brand-sky)]" />
          {paired ? "Mettre à jour l'appairage" : "Appairer ce site"}
        </h2>
        <p className="text-xs text-[hsl(var(--muted-foreground))] mb-5">
          Seuls <code>hub_url</code> et <code>pairing_code</code> sont stockés. Aucun secret métier (Stripe, SMTP, LLM) n'est partagé.
        </p>

        <div className="space-y-4">
          <div>
            <Label htmlFor="hub-url">URL du HUB</Label>
            <Input
              id="hub-url"
              placeholder="https://brand-studio-core.preview.emergentagent.com"
              value={hubUrl}
              onChange={(e) => setHubUrl(e.target.value)}
              data-testid="agency-hub-url-input"
            />
          </div>
          <div>
            <Label htmlFor="pairing-code">Code d'appairage</Label>
            <Input
              id="pairing-code"
              placeholder="N93DVJ3V"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              className="font-mono tracking-wider"
              data-testid="agency-pairing-code-input"
            />
          </div>
          <div className="flex flex-wrap gap-2 pt-2">
            <Button
              onClick={submit}
              disabled={busy || !hubUrl.trim() || !code.trim()}
              className="bg-[color:var(--brand-red)] hover:bg-[color:var(--brand-red)]/90"
              data-testid="agency-claim-btn"
            >
              <Link2 className="w-4 h-4 mr-1.5" />
              {paired ? "Réappairer" : "Appairer ce site"}
            </Button>
            {paired && (
              <>
                <Button onClick={heartbeat} disabled={busy} variant="outline" data-testid="agency-heartbeat-btn">
                  <Activity className="w-4 h-4 mr-1.5" /> Forcer un heartbeat
                </Button>
                <Button onClick={unpair} disabled={busy} variant="ghost" className="text-red-600 hover:text-red-700" data-testid="agency-unpair-btn">
                  <Unlink className="w-4 h-4 mr-1.5" /> Désappairer
                </Button>
              </>
            )}
          </div>
        </div>
      </Card>

      {/* Diagnostic */}
      {status?.last_ack_message && !paired && (
        <Card className="p-5 border-red-200 bg-red-50/40">
          <div className="text-xs font-bold uppercase tracking-wider text-red-700 mb-1">Diagnostic dernière tentative</div>
          <div className="text-sm text-red-900 break-all">
            HTTP {status.last_ack_status || "?"} — {status.last_ack_message}
          </div>
        </Card>
      )}
    </div>
  );
}
